import React, { useState, useEffect, useContext } from "react";
import Layout from "../../components/Layout";
import PageContainer from "../../components/common/PageContainer";
import useApi from "../../hooks/useApi";
import { AuthContext } from "../../contexts/AuthContext";
import { BASE_URL } from "../../config/api";

const LABEL_SIZES = {
  small: { cssW: "3in", cssH: "1.5in", labelText: "Small (3 x 1.5 in)" },
  large: { cssW: "4in", cssH: "2in", labelText: "Large (4 x 2 in)" },
};

const DEFAULT_SYMBOLOGY = "code128";

const GenerateLabelsPage = () => {
  const api = useApi();
  const { user } = useContext(AuthContext);
  const { settings, token } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selection, setSelection] = useState({}); // key -> { copies, include, product }
  const [labelSize, setLabelSize] = useState("small");
  const [symbology, setSymbology] = useState(DEFAULT_SYMBOLOGY);
  const [includeLogo, setIncludeLogo] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoadingProducts(true);
      try {
        const resp = await api.get("products", { queryParams: { noPagination: true } });
        const rows = Array.isArray(resp) ? resp : resp?.data ?? resp?.products ?? [];
        setProducts(rows);
        const init = {};
        (rows || []).forEach((p) => {
          const key = p._id || p.id || p.sku;
          init[key] = { copies: 1, include: false, product: p };
        });
        setSelection(init);
      } catch (err) {
        console.error("Failed to load products", err);
      } finally {
        setLoadingProducts(false);
      }
    };
    load();
  }, []);

  const toggleInclude = (key) => {
    setSelection((s) => ({ ...s, [key]: { ...(s[key] || {}), include: !(s[key]?.include || false) } }));
  };
  const setCopies = (key, copies) => {
    const c = Math.max(1, Number(copies) || 1);
    setSelection((s) => ({ ...s, [key]: { ...(s[key] || {}), copies: c } }));
  };

  const buildPayload = () => {
    const productsPayload = [];
    Object.keys(selection).forEach((key) => {
      const sel = selection[key];
      if (!sel?.include) return;
      const p = sel.product || {};
      productsPayload.push({
        sku: p.sku ?? p.SKU ?? p._id ?? "",
        name: p.name ?? p.title ?? "",
        price: typeof p.price === "number" ? p.price : parseFloat(p.price) || 0,
        copies: Number(sel.copies) || 1,
      });
    });

    return {
      products: productsPayload,
      symbology,
      logoUrl: includeLogo ? (user?.profilePicture || user?.profile_pic || "") : "",
      labelSize,
    };
  };

  const generatePreview = () => {
    const body = buildPayload();
    const expanded = [];
    (body.products || []).forEach((p) => {
      for (let i = 0; i < (p.copies || 1); i++) {
        expanded.push({ ...p, labelSize: body.labelSize });
      }
    });
    setPreviewItems(expanded);
  };

  const generatePdfFromServer = async () => {
    const body = buildPayload();
    if (!body.products || body.products.length === 0) {
      alert("Select at least one product to generate labels.");
      return;
    }
    setPdfLoading(true);
    try {
      // prefer explicit fetch to correctly handle PDF blob + credentials/CORS
      // determine base URL from api wrapper if available, otherwise use relative path
    
      const url = `${BASE_URL}/labels/generate`

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/pdf",
          'Authorization': `Bearer ${token}`,
        },
        // include credentials if server expects cookies / credentials (server shows Access-Control-Allow-Credentials: true)
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(text || `Request failed (${resp.status})`);
      }

      const blob = await resp.blob();
      const contentType = blob.type || resp.headers.get("content-type") || "";
      if (!contentType.includes("pdf") && !resp.headers.get("content-disposition")) {
        // server returned non-pdf (likely an error html/json) — show brief info
        const txt = await blob.text().catch(() => "");
        throw new Error(txt || "Unexpected non-PDF response from server");
      }

      const objectUrl = URL.createObjectURL(blob);
      // open new tab for user to view / download PDF
      window.open(objectUrl, "_blank", "noopener");
      // revoke later
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      console.error("Generate labels PDF failed:", err);
      alert("Unable to generate PDF. See console for details.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <Layout>
      <PageContainer title="Generate Labels">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 bg-white p-4 rounded shadow-sm">
            <h2 className="font-semibold mb-3">Select products</h2>
            {loadingProducts ? (
              <div>Loading products...</div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-auto pr-2">
                {products.length === 0 && <div className="text-sm text-gray-500">No products found.</div>}
                {products.map((p) => {
                  const key = p._id || p.id || p.sku;
                  const sel = selection[key] || { copies: 1, include: false, product: p };
                  return (
                    <div key={key} className="flex items-center justify-between border rounded p-2">
                      <div className="flex items-start gap-3">
                        <input type="checkbox" checked={!!sel.include} onChange={() => toggleInclude(key)} className="mt-1" />
                        <div>
                          <div className="font-medium">{p.name || p.title || "Unnamed"}</div>
                          <div className="text-sm text-gray-500">{p.sku ?? p.SKU ?? ""} • {p.categoryName || p.category?.name || ""}</div>
                          <div className="text-sm text-gray-700">Price: {typeof p.price === "number" ? p.price.toFixed(2) : p.price}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          value={sel.copies}
                          onChange={(e) => setCopies(key, e.target.value)}
                          className="w-20 border rounded px-2 py-1 text-sm"
                          aria-label={`Copies for ${p.name}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white p-4 rounded shadow-sm">
            <h2 className="font-semibold mb-3">Label options</h2>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Label size</label>
              <select value={labelSize} onChange={(e) => setLabelSize(e.target.value)} className="w-full border rounded px-2 py-1">
                <option value="small">{LABEL_SIZES.small.labelText}</option>
                <option value="large">{LABEL_SIZES.large.labelText}</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Symbology</label>
              <select value={symbology} onChange={(e) => setSymbology(e.target.value)} className="w-full border rounded px-2 py-1">
                <option value="code128">Code128</option>
                <option value="ean13">EAN-13</option>
                <option value="upc">UPC</option>
              </select>
            </div>

            <div className="mb-4 flex items-center gap-3">
              <input id="includeLogo" type="checkbox" checked={includeLogo} onChange={(e) => setIncludeLogo(e.target.checked)} />
              <label htmlFor="includeLogo" className="text-sm">
                Include logo (from profile)
              </label>
            </div>

            <div className="space-y-2">
              <button onClick={generatePreview} className="w-full bg-indigo-600 text-white px-4 py-2 rounded">Preview</button>
              <button onClick={() => setPreviewItems([])} className="w-full border px-4 py-2 rounded">Clear Preview</button>
              <button
                onClick={generatePdfFromServer}
                disabled={pdfLoading || Object.keys(selection).filter(k => selection[k]?.include).length === 0}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded"
              >
                {pdfLoading ? "Generating PDF..." : "Generate PDF (server)"}
              </button>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-medium mb-2">Preview</h2>
          <div className="flex flex-wrap gap-4">
            {previewItems.length === 0 && <div className="p-6 text-sm text-gray-500 bg-white rounded-md">No preview items.</div>}
            {previewItems.map((lab, idx) => {
              const sizeCfg = LABEL_SIZES[lab.labelSize || "small"];
              return (
                <div key={idx} className="label p-2 bg-white rounded shadow-sm" style={{ width: sizeCfg.cssW, height: sizeCfg.cssH }}>
                  <div className="flex items-center justify-between h-full">
                    <div>
                      <div className="font-bold">{lab.name}</div>
                      <div className="text-xs text-gray-600">{lab.sku}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">${Number(lab.price).toFixed(2)}</div>
                      <div className="text-xs text-gray-500">{lab.labelSize}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </PageContainer>
    </Layout>
  );
};

export default GenerateLabelsPage;